import React, { useState } from 'react';
import { Button, Checkbox, Dropdown, Empty, Modal, Space, Spin, Switch, Table, message } from 'antd';
import { DownOutlined, DownloadOutlined, SettingOutlined } from '@ant-design/icons';
import { downloadPartitionFile, downloadRenderedPartitionFile, getPartitionRenderConfig } from '../Feature/API';
import {
    buildRenderedDownloadRequest,
    getDefaultTrackSelection,
    getDownloadFormatLabel,
    getPartitionDownloadFormats,
} from '../Utils/songPartitions';

export const triggerBrowserDownload = (blob, fileName) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Give the browser a tick to start the download before revoking.
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};

const errorText = (error) => error?.response?.status
    ? `${error.response.status} ${error.response.statusText || ''}`.trim()
    : (error?.message || 'Unknown error');

export const PartitionDownloadButton = ({ partition, songId, spaceId }) => {
    const [downloading, setDownloading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [configLoading, setConfigLoading] = useState(false);
    const [renderConfig, setRenderConfig] = useState(null);
    const [selection, setSelection] = useState({});
    const [conductor, setConductor] = useState(false);
    const [landscape, setLandscape] = useState(false);

    const formats = getPartitionDownloadFormats(partition);
    const canRender = formats.includes('pdf');

    const download = async (format) => {
        setDownloading(true);
        try {
            const { blob, fileName } = await downloadPartitionFile(spaceId, songId, partition.id, format);
            triggerBrowserDownload(blob, fileName);
        } catch (error) {
            message.error(`Download failed: ${errorText(error)}`);
        } finally {
            setDownloading(false);
        }
    };

    const openCustomPdf = async () => {
        setModalOpen(true);
        setConfigLoading(true);
        try {
            const config = await getPartitionRenderConfig(spaceId, songId, partition.id);
            setRenderConfig(config);
            setSelection(getDefaultTrackSelection(config?.tracks));
            setConductor(Boolean(config?.conductor));
            setLandscape(Boolean(config?.landscape));
        } catch (error) {
            message.error(`Could not load export settings: ${errorText(error)}`);
            setModalOpen(false);
        } finally {
            setConfigLoading(false);
        }
    };

    const request = buildRenderedDownloadRequest({
        tracks: renderConfig?.tracks || [],
        selection,
        conductor,
        landscape,
    });

    const downloadCustomPdf = async () => {
        setDownloading(true);
        try {
            const { blob, fileName } = await downloadRenderedPartitionFile(spaceId, songId, partition.id, request);
            triggerBrowserDownload(blob, fileName);
            setModalOpen(false);
        } catch (error) {
            message.error(`PDF generation failed: ${errorText(error)}`);
        } finally {
            setDownloading(false);
        }
    };

    const toggle = (trackIndex, key, value) => {
        setSelection((current) => ({
            ...current,
            [trackIndex]: { ...current[trackIndex], [key]: value },
        }));
    };

    const setAll = (value) => {
        setSelection(Object.fromEntries((renderConfig?.tracks || []).map((track) => [
            track.index,
            { notation: value, tablature: value && track.fretted !== false },
        ])));
    };

    if (formats.length <= 1) {
        return (
            <Button icon={<DownloadOutlined />} loading={downloading} onClick={() => download('original')}>
                Download
            </Button>
        );
    }

    const menu = {
        items: [
            ...formats.map((format) => ({
                key: format,
                icon: <DownloadOutlined />,
                label: getDownloadFormatLabel(format, partition),
            })),
            ...(canRender ? [
                { type: 'divider' },
                { key: '__custom_pdf', icon: <SettingOutlined />, label: 'Custom PDF…' },
            ] : []),
        ],
        onClick: ({ key }) => (key === '__custom_pdf' ? openCustomPdf() : download(key)),
    };

    const columns = [
        { title: 'Track', key: 'name', render: (_, track) => track.name || `Track ${track.index + 1}` },
        { title: 'Instrument', dataIndex: 'instrument', key: 'instrument', responsive: ['sm'] },
        {
            title: 'Notation',
            key: 'notation',
            align: 'center',
            render: (_, track) => (
                <Checkbox
                    checked={Boolean(selection[track.index]?.notation)}
                    onChange={(e) => toggle(track.index, 'notation', e.target.checked)}
                />
            ),
        },
        {
            title: 'Tabs',
            key: 'tablature',
            align: 'center',
            render: (_, track) => (
                <Checkbox
                    disabled={track.fretted === false}
                    checked={track.fretted !== false && Boolean(selection[track.index]?.tablature)}
                    onChange={(e) => toggle(track.index, 'tablature', e.target.checked)}
                />
            ),
        },
    ];

    return (
        <>
            <Dropdown menu={menu} trigger={['click']} disabled={downloading}>
                <Button icon={<DownloadOutlined />} loading={downloading}>
                    Download <DownOutlined />
                </Button>
            </Dropdown>
            <Modal
                title={`Custom PDF — ${partition.file_name}`}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={downloadCustomPdf}
                okText="Download PDF"
                okButtonProps={{ disabled: configLoading || request.tracks.length === 0 }}
                confirmLoading={downloading}
                destroyOnHidden
                width={600}
            >
                {configLoading ? (
                    <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
                ) : (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Space wrap size="large">
                            <Space><Switch checked={conductor} onChange={setConductor} /> Conductor score</Space>
                            <Space><Switch checked={landscape} onChange={setLandscape} /> Landscape</Space>
                        </Space>
                        {renderConfig?.tracks?.length ? (
                            <>
                                <Space>
                                    <Button size="small" onClick={() => setAll(true)}>Select all</Button>
                                    <Button size="small" onClick={() => setAll(false)}>Clear</Button>
                                </Space>
                                <Table
                                    size="small"
                                    rowKey="index"
                                    columns={columns}
                                    dataSource={renderConfig.tracks}
                                    pagination={false}
                                />
                                {request.tracks.length === 0 && (
                                    <div style={{ color: '#cf1322' }}>Select at least one track.</div>
                                )}
                            </>
                        ) : (
                            <Empty description="No tracks found in this score" />
                        )}
                    </Space>
                )}
            </Modal>
        </>
    );
};


